import {QuoteStatus} from "@/api/data-contracts";
import {AppThemeContextProps} from "@/app/AppThemeContext";
import {LabelValue} from "@/lib/components/LabelValueList";
import { CommonI18N } from "@/translations";
import {color_blue, color_green, color_grey, color_orange, color_purple} from "@/agentur/AgenturColors";

interface QuoteStatusField {
    quoteStatus?: QuoteStatus | null;
    statusValue?: string | null;
}

/**
 * Generates a field object for quote status with a label, value, tooltip, and color.
 *
 * @param {QuoteStatusField} status - The status object containing the quote status and status value.
 * @param {string} label - The label for the field.
 * @param {AppThemeContextProps} theme - The theme context properties.
 * @param {string} [defaultValue=""] - The default value to use if the status value is not available.
 * @param {string} [tooltipKey] - The key for the tooltip text.
 * @returns {LabelValue} The field object with label, value, tooltipKey, and color properties.
 */
export const quoteStatusField = (
    status: QuoteStatusField,
    label: string,
    theme: AppThemeContextProps,
    defaultValue?: QuoteStatusField,
    tooltipKey?: string
): LabelValue => ({
    label,
    value: status?.quoteStatus && status?.statusValue ? status?.statusValue : (defaultValue?.statusValue ?? ""),
    tooltipKey,
    color: getQuoteStatusColor(theme.mode, status.quoteStatus ?? undefined)
});

/**
 * Returns the color associated with a given quote status.
 *
 * @returns {string} The color corresponding to the given status code.
 * @param mode
 * @param status
 */
export const getQuoteStatusColor = (mode: "light" | "dark", status?: QuoteStatus): string => {
    switch (status) {
        case QuoteStatus.NOT_VACANT:
            return color_green[3];
        case QuoteStatus.VACANT:
            return color_green[4];
        case QuoteStatus.SUBMITTED:
            return color_purple[mode === "dark" ? 2 : 4];
        case QuoteStatus.ACCEPTED:
            return color_green[mode === "dark" ? 0 : 2];
        case QuoteStatus.DISMISSED:
            return color_orange[mode === "dark" ? "A" : 0];
        case QuoteStatus.REJECTED:
            return color_purple[3];
        case QuoteStatus.IN_NEGOTIATION:
            return color_blue[3];
        default:
            return color_grey[3];
    }
};

export const getQuoteStatusText = (t: (key: string) => string, status?: QuoteStatus): string =>
    (status && t(CommonI18N.enum.quoteStatus[status])) ?? t(CommonI18N.message.notGiven);
